import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services/api.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatChipGrid, MatChipRow, MatChipRemove, MatChipInput } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatAutocompleteTrigger, MatAutocomplete, MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-company-product-team-credit-dialog',
  templateUrl: './company-product-team-credit-dialog.component.html',
  styleUrl: './company-product-team-credit-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatChipGrid,
    MatChipRow,
    MatChipRemove,
    MatIcon,
    MatChipInput,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatDialogActions,
    MatButton,
    AsyncPipe,
  ],
})
export class CompanyProductTeamCreditDialogComponent implements OnInit {
  data = inject(MAT_DIALOG_DATA);
  dialogRef = inject<MatDialogRef<CompanyProductTeamCreditDialogComponent>>(MatDialogRef);
  protected fb = inject(UntypedFormBuilder);
  private api = inject(ApiService);

  form: UntypedFormGroup;

  users: User[] = [];
  usersSearchCtrl = new FormControl('');
  filteredUsers: Observable<User[]>;

  get isCreate() {
    return this.data.company_product_team.id == null;
  }

  constructor() {
    this.form = this.fb.group({
      name: ['', Validators.compose([Validators.required, Validators.minLength(5)])],
      company_product_id: ['', Validators.required],
      users: [[], Validators.compose([Validators.required, Validators.minLength(1)])],
    });

    this.initFilteredUsers();
  }

  ngOnInit(): void {
    this.patchInput();
    this.fetchUsers();
  }

  onCancel() {
    this.dialogRef.close();
  }

  removeUser(idx: number) {
    const users: User[] = this.form.value.users;
    users.splice(idx, 1);
    this.form.controls['users'].patchValue(users);
  }

  selectedUser(user: User) {
    const users: User[] = this.form.value.users;
    users.push(user);
    this.form.controls['users'].patchValue(users);
    this.usersSearchCtrl.setValue('');
  }

  submit() {
    const data = { ...this.form.value };
    data.users = data.users.map((u: User) => u.id);
    if (this.isCreate) {
      this.api.createCompanyProductTeam(data).subscribe(data => {
        this.dialogRef.close(data);
      });
    } else {
      this.api.updateCompanyProductTeam(this.data.company_product_team.id, data).subscribe(data => {
        this.dialogRef.close(data);
      });
    }
  }

  private initFilteredUsers() {
    this.filteredUsers = this.usersSearchCtrl.valueChanges.pipe(
      startWith(null),
      map((user: string | null) => (user && typeof user === 'string' ? this._filterUsers(user) : this.users.slice()))
    );
  }

  private _filterUsers(user: string): User[] {
    const filterValue = user.toLowerCase();
    return this.users.filter(user => user.name.toLowerCase().includes(filterValue));
  }

  private patchInput() {
    this.form.patchValue(this.data.company_product_team);
  }

  private fetchUsers() {
    this.api.getActiveCompanyProductMembersWithBusiness(this.form.value.company_product_id).subscribe(data => {
      this.users = data.bisual_users.map(m => m.user).concat(data.company_users);
    });
  }
}
